import {
    createSlice, createAsyncThunk, createEntityAdapter,
    EntityState, PayloadAction
} from '@reduxjs/toolkit';
import { apiFetch } from '../../../lib/api';
import type { RootState } from '../../index';

export interface Project {
    id: string;
    orgId?: string | null;
    name: string;
    color: string;
    code: string;
    brief?: string | null;
    definitionOfDone?: string | null;
    stakeholderNotes?: string | null;
    operatingRules?: string | null;
    duplicatePreventionRules?: string | null;
    teamNodeId?: string | null;
    plannedStartAt?: string | null;
    plannedEndAt?: string | null;
    timezone?: string | null;
    ownerUid?: string | null;
    clientId?: string | null;
    ownerUserId?: string | null;
    milestones?: ProjectMilestone[];
    createdAt?: any;
    updatedAt?: any;
}

export interface ProjectMilestone {
    id: string;
    projectId: string;
    title: string;
    outcome?: string | null;
    definitionOfDone?: string | null;
    notesBlockers?: string | null;
    plannedStartAt?: string | null;
    plannedEndAt?: string | null;
    timezone?: string | null;
    createdAt?: any;
    updatedAt?: any;
}

const adapter = createEntityAdapter<Project>();

interface ProjectsState {
    items: EntityState<Project, string>;
    loading: boolean;
    creating: boolean;
    error: string | null;
}
const initialState: ProjectsState = {
    items: adapter.getInitialState(),
    loading: false,
    creating: false,
    error: null,
};

export const fetchProjects = createAsyncThunk<
    Project[],
    { clientUid?: string; teamNodeId?: string } | void
>(
    'projects/fetch',
    async (params) => {
        const qs = new URLSearchParams();
        if (params?.clientUid) qs.set('clientUid', params.clientUid);
        if (params?.teamNodeId) qs.set('teamNodeId', params.teamNodeId);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const res = await apiFetch<{ projects: Project[] }>(`/api/projects${suffix}`);
        return res.projects || [];
    }
);


// NEW
export const fetchProjectsByIds = createAsyncThunk<Project[], string[]>(
    'projects/fetchByIds',
    async (ids) => {
        if (!ids?.length) return [];
        const usp = new URLSearchParams({ ids: ids.slice(0, 100).join(',') }); // keep it safe
        const res = await apiFetch<{ projects: Project[] }>(`/api/projects?${usp.toString()}`);
        return res.projects || [];
    }
);





export const createProject = createAsyncThunk<
    { id: string },
    { name: string; color: string; code?: string; clientId?: string; teamNodeId?: string; orgId?: string; timezone?: string | null; brief?: string | null; definitionOfDone?: string | null; stakeholderNotes?: string | null; operatingRules?: string | null; duplicatePreventionRules?: string | null; plannedStartAt?: string | null; plannedEndAt?: string | null; startDate?: string | null; endDate?: string | null }
>('projects/create', async (payload) => {
    return apiFetch<{ id: string }>('/api/projects', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
});

export const updateProject = createAsyncThunk<
    { project: Project },
    {
        projectId: string;
        patch: {
            name?: string;
            brief?: string | null;
            definitionOfDone?: string | null;
            stakeholderNotes?: string | null;
            operatingRules?: string | null;
            duplicatePreventionRules?: string | null;
            startDate?: string | null;
            endDate?: string | null;
            plannedStartAt?: string | null;
            plannedEndAt?: string | null;
            timezone?: string | null;
        };
    }
>('projects/updateProject', async ({ projectId, patch }) => {
    return apiFetch<{ project: Project }>(`/api/projects/${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
    });
});

export const duplicateProject = createAsyncThunk<
    { id: string },
    { projectId: string; name?: string }
>('projects/duplicate', async ({ projectId, name }) => {
    return apiFetch<{ id: string }>(`/api/projects/${projectId}/duplicate`, {
        method: 'POST',
        body: JSON.stringify(name ? { name } : {}),
    });
});

export const createProjectMilestone = createAsyncThunk<
    { projectId: string; milestone: ProjectMilestone },
    { projectId: string; title: string; outcome?: string | null; definitionOfDone?: string | null; notesBlockers?: string | null; startDate?: string | null; endDate?: string | null; plannedStartAt?: string | null; plannedEndAt?: string | null }
>('projects/createMilestone', async ({ projectId, ...payload }) => {
    const response = await apiFetch<{ milestone: ProjectMilestone }>(`/api/projects/${projectId}/milestones`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return {
        projectId,
        milestone: response.milestone,
    };
});

export const updateProjectMilestone = createAsyncThunk<
    { projectId: string; milestone: ProjectMilestone },
    { projectId: string; milestoneId: string; patch: { title?: string; outcome?: string | null; definitionOfDone?: string | null; notesBlockers?: string | null; startDate?: string | null; endDate?: string | null; plannedStartAt?: string | null; plannedEndAt?: string | null; timezone?: string | null } }
>('projects/updateMilestone', async ({ projectId, milestoneId, patch }) => {
    const response = await apiFetch<{ milestone: ProjectMilestone }>(`/api/projects/${projectId}/milestones/${milestoneId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
    });
    return {
        projectId,
        milestone: response.milestone,
    };
});

export const deleteProjectMilestone = createAsyncThunk<
    { projectId: string; milestoneId: string; childTicketCount: number; ticketHandling: 'delete_tickets' | 'transfer_to_project' | null },
    { projectId: string; milestoneId: string; ticketHandling: 'delete_tickets' | 'transfer_to_project' }
>('projects/deleteMilestone', async ({ projectId, milestoneId, ticketHandling }) => {
    const response = await apiFetch<{
        ok: boolean;
        projectId: string;
        milestoneId: string;
        childTicketCount: number;
        ticketHandling: 'delete_tickets' | 'transfer_to_project' | null;
    }>(`/api/projects/${projectId}/milestones/${milestoneId}`, {
        method: 'DELETE',
        body: JSON.stringify({ ticketHandling }),
    });
    return {
        projectId: response.projectId,
        milestoneId: response.milestoneId,
        childTicketCount: response.childTicketCount ?? 0,
        ticketHandling: response.ticketHandling ?? null,
    };
});

export const deleteProject = createAsyncThunk<
    { projectId: string; childTicketCount: number; ticketHandling: 'delete_tickets' | 'keep_tickets' | null },
    { projectId: string; ticketHandling: 'delete_tickets' | 'keep_tickets' }
>('projects/deleteProject', async ({ projectId, ticketHandling }) => {
    const response = await apiFetch<{
        ok: boolean;
        projectId: string;
        childTicketCount: number;
        ticketHandling: 'delete_tickets' | 'keep_tickets' | null;
    }>(`/api/projects/${projectId}`, {
        method: 'DELETE',
        body: JSON.stringify({ ticketHandling }),
    });
    return {
        projectId: response.projectId,
        childTicketCount: response.childTicketCount ?? 0,
        ticketHandling: response.ticketHandling ?? null,
    };
});

const slice = createSlice({
    name: 'projects',
    initialState,
    reducers: { clearProjectsError(s) { s.error = null; } },
    extraReducers: (b) => {
        b.addCase(fetchProjects.pending, (s) => { s.loading = true; s.error = null; });
        b.addCase(fetchProjects.fulfilled, (s, a: PayloadAction<Project[]>) => {
            s.loading = false;
            adapter.setAll(s.items, a.payload);
        });
        b.addCase(fetchProjects.rejected, (s, a) => {
            s.loading = false; s.error = a.error.message || 'Failed to load projects';
        });

        b.addCase(createProject.pending, (s) => { s.creating = true; s.error = null; });
        b.addCase(createProject.fulfilled, (s) => { s.creating = false; });
        b.addCase(createProject.rejected, (s, a) => {
            s.creating = false; s.error = a.error.message || 'Failed to create project';
        });
        b.addCase(updateProject.fulfilled, (s, a) => {
            adapter.upsertOne(s.items, a.payload.project);
        });
        b.addCase(duplicateProject.pending, (s) => { s.creating = true; s.error = null; });
        b.addCase(duplicateProject.fulfilled, (s) => { s.creating = false; });
        b.addCase(duplicateProject.rejected, (s, a) => {
            s.creating = false; s.error = a.error.message || 'Failed to duplicate project';
        });
        b.addCase(createProjectMilestone.fulfilled, (s, a) => {
            const project = s.items.entities[a.payload.projectId];
            if (!project) return;
            const milestones = Array.isArray(project.milestones) ? project.milestones : [];
            project.milestones = [
                a.payload.milestone,
                ...milestones.filter((entry) => entry.id !== a.payload.milestone.id),
            ];
        });
        b.addCase(updateProjectMilestone.fulfilled, (s, a) => {
            const project = s.items.entities[a.payload.projectId];
            if (!project) return;
            const milestones = Array.isArray(project.milestones) ? project.milestones : [];
            project.milestones = milestones.map((entry) =>
                entry.id === a.payload.milestone.id ? a.payload.milestone : entry,
            );
        });
        b.addCase(deleteProjectMilestone.fulfilled, (s, a) => {
            const project = s.items.entities[a.payload.projectId];
            if (!project) return;
            const milestones = Array.isArray(project.milestones) ? project.milestones : [];
            project.milestones = milestones.filter((entry) => entry.id !== a.payload.milestoneId);
        });
        b.addCase(deleteProject.fulfilled, (s, a) => {
            adapter.removeOne(s.items, a.payload.projectId);
        });
        b.addCase(fetchProjectsByIds.fulfilled, (s, a: PayloadAction<Project[]>) => {
            adapter.upsertMany(s.items, a.payload);
        });
    }
});

export const projectsReducer = slice.reducer;
export const { clearProjectsError } = slice.actions;

const selectors = adapter.getSelectors<RootState>((s) => s.projects.items);
export const selectAllProjects = selectors.selectAll;
export const selectProjectsLoading = (s: RootState) => s.projects.loading;
export const selectProjectsCreating = (s: RootState) => s.projects.creating;
